// client/src/pages/visitor/FormularioCliente.jsx

import { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { createClientApi } from "../../api/clientAccess";
import {
  NotificationContainer,
  NotificationManager,
} from "react-notifications";
import styles from "../../App.module.css";
import {
  useFormState,
  PersonalInformation1,
  InformacoesDeViagem,
  InformacoesFamilia,
  OcupacaoAtual,
  EnderecoEContato,
  DadosDePassaporte,
  FormacoesAcademicas,
  InformacoesAdicionais,
  ResidenciaEVistosEUA,
} from "./FormSteps";
import {
  InformacoesPessoaisPassport,
  EnderecoEContatoPassport,
  InformacoesAdicionaisPassport,
} from "./PassportStep";
import {
  InformacoesPessoaisCanada,
  InformacoesFamiliaCanada,
  OcupacaoAnteriorCanada,
  InformacoesDeViagemCanada,
  EnderecoEContatoCanada,
  InformacoesAdicionaisCanada,
  DocumentosCanada,
  InformacoesComplementaresCanada,
  OcupacaoAtualCanada,
  DadosDePassaporteIdentidade,
} from "./FormStepsCanada";

function FormularioCliente() {
  const { formularioId: formId } = useParams();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("acesso");
  const api = useMemo(() => createClientApi(linkToken), [linkToken]);
  const [loadState, setLoadState] = useState({});
  const formReady =
    loadState.formId === formId &&
    loadState.linkToken === linkToken &&
    loadState.ready;
  const [formData, setFormField] = useFormState() || [{}, () => {}]; // Garante valores padrão caso useFormState retorne undefined
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [clientId, setClientId] = useState(0);
  const [initialFormData, setInitialFormData] = useState({}); // Estado para armazenar os dados iniciais
  const [tipoDs, setTipoDs] = useState("");
  const [returningToClient, setReturningToClient] = useState(false);

  const voltarParaAreaCliente = async () => {
    if (linkToken) {
      window.location.assign(`/formulario/${encodeURIComponent(linkToken)}`);
      return;
    }
    if (!clientId || returningToClient) return;
    setReturningToClient(true);
    try {
      const { data } = await api.get(`/cliente/token-por-cliente/${clientId}`);
      if (!data.status || !data.token) {
        NotificationManager.error("Link da ?rea do cliente n?o encontrado.");
        return;
      }
      window.location.assign(`/formulario/${encodeURIComponent(data.token)}`);
    } catch {
      NotificationManager.error(
        "N?o foi poss?vel abrir a ?rea do cliente. Tente novamente.",
      );
    } finally {
      setReturningToClient(false);
    }
  };

  const [steps, setSteps] = useState([
    "Dados Pessoais",
    "Informações de Viagem",
    "Informações de Contatos nos EUA",
    "Informações de Família",
    "Endereço e Contato",
    "Dados de Passaporte",
    "Ocupação Atual",
    "Formações Acadêmicas",
    "Informações Adicionais",
  ]);

  const [requiredFields, setRequiredFields] = useState({
    0: ["nome", "sobrenome", "cidadeNascimento", "dataNascimento", "cpf"],
    1: ["dataChegada", "cidadePretendida"],
    2: ["teveVisto", "esteveEUA", "vistoNegado"],
    3: ["endereco", "bairro", "cep", "cidadeEstado"],
    4: [
      "dataEmissaoPassaporte",
      "dataExpiracaoPassaporte",
      "numeroPassaporte",
      "cidadeEstadoEmissaoPassaporte",
    ],
    5: ["parenteImediatoEUA"],
    6: ["ocupacaoAtual", "empresa"],
    7: [],
    8: ["participaOrganizacoes", "temHabilidadesEspeciais", "temCondenacoes"],
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!formId) return;
    let active = true;
    setLoadState({ formId, linkToken, ready: false });

    const applyFormulario = (data) => {
      setFormField(data.formularioData);
      setClientId(data.ClienteId);
      setTipoDs(data.tipo);
      setInitialFormData(data.formularioData);

      if (data.tipo === "kid") {
        setSteps([
          "Dados Pessoais",
          "Informações de Viagem",
          "Informações de Contatos nos EUA",
          "Informações de Família",
          "Endereço e Contato",
          "Dados de Passaporte",
        ]);
      } else if (data.tipo === "canada") {
        setSteps([
          "Dados Pessoais",
          "Endereço e Contato",
          "Dados de Passaporte",
          "Informações de Família",
          "Informacoes De Viagem",
          "Vistos E Permanências",
          "Ocupação Atual",
          "Ocupação Anterior",
          "Documentos Adicionais",
          "Informações adcionais",
        ]);

        setRequiredFields({
          0: ["nome", "sobrenome", "cidadeNascimento", "dataNascimento", "cpf"],
          1: ["endereco", "cep", "cidadeEstado"],
          2: [
            "dataEmissaoPassaporte",
            "dataExpiracaoPassaporte",
            "numeroPassaporte",
            "cidadeEstadoEmissaoPassaporte",
            "numeroIdentidade",
            "dataEmissaoIdentidade",
            "cidadeEstadoEmissaoIdentidade",
          ],
          3: [],
          4: [
            "motivoViagemCanada",
            "dataPretendidaViagem",
            "cidadePretendidaCanada",
          ],
          5: [],
          6: ["ocupacaoAtual", "dataInicio"],
          7: [],
          8: [],
          9: [],
        });
      } else if (data.tipo === "passport") {
        setSteps([
          "Informações Pessoais",
          "Endereço e Contato",
          "Informações Adicionais",
        ]);

        setRequiredFields({
          0: ["nome", "cpf", "dataNascimento"],
          1: ["endereco", "email", "telefone", "cep"],
          2: ["jaTevePassaporte", "formaPagamento", "postoPF"],
        });
      }
    };

    const loadFormulario = async () => {
      try {
        const { data } = await api.get(`/formulario/${formId}`);
        if (!active) return;
        if (!data.success || !data.formularioData)
          throw new Error("Formulário indisponível.");
        applyFormulario(data);
        setCurrentStep(0);
        setLoadState({ formId, linkToken, ready: true });
      } catch (error) {
        if (!active) return;
        setLoadState({ formId, linkToken, ready: false, error: true });
        NotificationManager.error(
          "Não foi possível abrir o formulário. Use o link individual enviado pela assessoria.",
        );
      }
    };

    loadFormulario();
    return () => {
      active = false;
    };
  }, [formId, linkToken, api]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    console.log("MONTADO FormularioCliente", formId);

    return () => {
      console.log("DESMONTADO FormularioCliente", formId);
    };
  }, [formId]);

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  };

  const saveFormData = async () => {
    if (!formReady) return;
    if (!hasChanges()) {
      console.log("Nenhuma alteração detectada. Salvamento ignorado.");
      return;
    }

    try {
      const response = await api.post("/formularios/salvar", {
        formId,
        formularioData: formData,
      });

      if (response.data.success) {
        NotificationManager.success("Dados salvos com sucesso.");
        setInitialFormData(formData); // Atualiza o estado inicial após o salvamento
      } else {
        NotificationManager.error("Erro ao salvar os dados.");
      }
    } catch (error) {
      NotificationManager.error("Erro ao salvar o formulário.");
      console.error("Erro ao salvar o formulário:", error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formReady) return;

    if (!validateForm()) {
      NotificationManager.error("Corrija os erros antes de salvar.");
      return;
    }

    try {
      const response = await api.post("/formularios/salvar", {
        formId,
        formularioData: formData,
      });

      if (response.data.success) {
        NotificationManager.success(response.data.message);
      } else {
        NotificationManager.error(response.data.message);
      }
    } catch (error) {
      console.error("Erro ao salvar formulário:", error);
      NotificationManager.error("Erro ao salvar formulário.");
    }
  };

  const handleInputChange = (e, index = null, section = null, field = null) => {
    const { name, value, type, checked } = e.target;

    // Verifica se o campo é um "desconhecido" (checkbox) e processa adequadamente
    if (name.startsWith("desconhece")) {
      const targetField = name.replace("desconhece", "").toLowerCase();
      setFormField({
        [name]: checked ? "sim" : "nao", // Define "sim" para desconhecido, "nao" para conhecido
        [targetField]: checked ? "" : formData[targetField], // Limpa o campo associado se marcado como desconhecido
      });
    }
    // Manipula seções (arrays), como parentes ou outras informações compostas
    else if (section && index !== null) {
      const updatedSection = [...formData[section]];
      if (field === "aindaEmpregado" || field === "aindaEstudando") {
        // Alterna o estado do checkbox e limpa ou restaura o campo de data
        updatedSection[index][field] = checked;
        if (!checked) {
          updatedSection[index].dataDemissao = ""; // Restaura a data ao desmarcar 'Ainda Empregado'
          updatedSection[index].dataTermino = ""; // Restaura a data ao desmarcar 'Ainda Estudando'
        }
      } else if (field) {
        updatedSection[index][field] = value; // Atualiza um campo específico em objetos dentro do array
      } else {
        updatedSection[index] = value; // Para arrays simples, como `emails` e `telefones`
      }
      setFormField(section, updatedSection);
    }
    // Manipula inputs normais
    else {
      setFormField(name, type === "checkbox" ? checked : value);
      // Adiciona um divórcio automaticamente se o estado civil for "divorciado"
      if (
        name === "estadoCivil" &&
        (value === "divorciado" || value === "separado")
      ) {
        if (formData.divorcios.length === 0) {
          setFormField("divorcios", [
            {
              nome: "",
              dataNascimento: "",
              localNascimento: "",
              dataCasamento: "",
              dataDivorcio: "",
              motivo: "",
            },
          ]);
        }
      }
      // Limpa divórcios se o estado civil mudar para algo diferente de "divorciado"
      if (
        name === "estadoCivil" &&
        value !== "divorciado" &&
        value !== "separado"
      ) {
        setFormField("divorcios", []);
      }
    }
    // **Validação de e-mail**
    if (type === "email") {
      if (!validateEmail(value)) {
        // Atualiza o estado de erros para este campo
        setErrors((prev) => ({ ...prev, [name]: "E-mail inválido" }));
      } else {
        // Remove o erro se o e-mail estiver correto
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  // Função auxiliar para validar e-mails
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validateForm = () => {
    const newErrors = {};
    const required = requiredFields[currentStep] || [];

    // console.log(`🧪 Validando etapa ${currentStep} com campos obrigatórios:`, required);

    required.forEach((field) => {
      const value = formData[field];
      const isEmpty = Array.isArray(value)
        ? value.length === 0
        : !value || value.trim() === "";

      if (isEmpty) {
        // console.log(`❌ Campo "${field}" está vazio ou inválido.`);
        newErrors[field] = "Campo obrigatório";
      } else {
        // console.log(`✅ Campo "${field}" está preenchido com:`, value);
      }
    });

    setErrors(newErrors);

    const valid = Object.keys(newErrors).length === 0;
    // console.log(`🔍 Resultado final da validação: ${valid ? 'VÁLIDO ✅' : 'INVÁLIDO ❌'}`);

    return valid;
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (
      formData.viagensInternacionais === "sim" &&
      !Array.isArray(formData.listaViagens)
    ) {
      setFormField("listaViagens", [
        { nome: "", dataIda: "", dataVolta: "", local: "", motivo: "" },
      ]);
    }
  }, [formData.viagensInternacionais, formData.listaViagens]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const addEntry = (section) => {
    const current = Array.isArray(formData[section]) ? formData[section] : [];

    if (
      (section === "telefones" || section === "emails") &&
      current.length >= 3
    ) {
      alert(
        `Você só pode adicionar até 3 ${section === "telefones" ? "números de celular" : "e-mails"}.`,
      );
      return;
    }
    if (section === "listaViagens" && current.length >= 20) {
      alert("Você só pode adicionar até 20 viagens.");
      return;
    }
    if (section === "viagensEUA" && current.length >= 10) {
      alert("Você só pode adicionar até 10 entradas de viagens aos EUA.");
      return;
    }

    let newEntry;
    switch (section) {
      case "listaViagens":
        newEntry = {
          nome: "",
          dataIda: "",
          dataVolta: "",
          local: "",
          motivo: "",
        };
        break;
      case "viagensEUA":
        newEntry = { dataEntradaEUA: "", duracaoEUA: "" };
        break;
      case "ocupacaoAnterior":
        newEntry = {
          empresa: "",
          endereco: "",
          telefone: "",
          cargo: "",
          dataAdmissao: "",
          dataDemissao: "",
          descricao: "",
        };
        break;
      case "formacaoAcademica":
        newEntry = {
          instituicao: "",
          endereco: "",
          curso: "",
          dataInicio: "",
          dataTermino: "",
        };
        break;
      case "acompanhantes":
        newEntry = { nome: "", sobrenome: "", parentesco: "" };
        break;
      case "parentesImediatos":
        newEntry = { nome: "", grauParentesco: "", situacao: "" };
        break;
      case "emails":
      case "telefones":
      case "idiomasFalados":
      case "viagensRecentes":
      case "itinerioCidades":
        newEntry = "";
        break;
      case "redesSociais":
        newEntry = { plataforma: "NONE", usuario: "" };
        break;
      case "divorcios":
        newEntry = {
          nome: "",
          sobrenome: "",
          dataNascimento: "",
          localNascimento: "",
          dataCasamento: "",
          dataDivorcio: "",
          motivo: "",
        };
        break;
      case "filhos":
        newEntry = {
          nome: "",
          sobrenome: "",
          sexo: "",
          dataNascimento: "",
          relacao: "",
          endereco: "",
        };
        break;
      default:
        console.warn(`Seção não reconhecida: ${section}`);
        return;
    }

    setFormField(section, [...current, newEntry]);
  };

  const removeEntry = (section, index) => {
    if (formData[section] && formData[section].length > 0) {
      const updatedSection = [...formData[section]];
      updatedSection.splice(index, 1);
      setFormField(section, updatedSection);
    } else {
      console.warn(`Não há itens para remover na seção: ${section}`);
    }
  };
  const isStepComplete = (stepIndex) => {
    const fields = requiredFields[stepIndex] || [];

    // Lógica para aba 7 (Formações Acadêmicas / Ocupação Anterior)
    if (fields.length === 0 && stepIndex === 7) {
      // Checa se PELO MENOS UM está preenchido
      const temFormacao = Array.isArray(formData.formacaoAcademica)
        ? formData.formacaoAcademica.length > 0
        : !!formData.formacaoAcademica &&
          formData.formacaoAcademica.trim() !== "";

      const temOcupacao = Array.isArray(formData.ocupacaoAnterior)
        ? formData.ocupacaoAnterior.length > 0
        : !!formData.ocupacaoAnterior &&
          formData.ocupacaoAnterior.trim() !== "";

      return temFormacao || temOcupacao;
    }

    // Lógica padrão para outros steps
    return fields.every((field) => {
      const value = formData[field];
      return Array.isArray(value)
        ? value.length > 0
        : value && value.trim() !== "";
    });
  };

  const isStepPartial = (stepIndex) => {
    const fields = requiredFields[stepIndex];
    return (
      fields.some((field) => {
        const value = formData[field];
        return Array.isArray(value)
          ? value.length > 0
          : value && value.trim() !== "";
      }) && !isStepComplete(stepIndex)
    );
  };

  const getStatusClass = (stepIndex) => {
    if (isStepComplete(stepIndex)) {
      return styles.completedStep;
    } else if (isStepPartial(stepIndex)) {
      return styles.partialStep;
    }
    return styles.uncompletedStep;
  };

  const nextStep = async () => {
    if (!validateForm()) {
      NotificationManager.error("Corrija os erros antes de salvar.");
      return;
    }
    await saveFormData(); // Salva os dados antes de avançar
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = async () => {
    await saveFormData(); // Salva os dados antes de voltar
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = async (stepIndex) => {
    await saveFormData(); // Salva os dados ao clicar diretamente em uma etapa
    setCurrentStep(stepIndex);
  };

  const renderStep = () => {
    if (tipoDs === "passport") {
      switch (currentStep) {
        case 0:
          return (
            <InformacoesPessoaisPassport
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />
          );
        case 1:
          return (
            <EnderecoEContatoPassport
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />
          );
        case 2:
          return (
            <InformacoesAdicionaisPassport
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
            />
          );
        default:
          return null;
      }
    } else if (tipoDs === "canada") {
      // Lógica específica para o formulário do Canadá, se necessário
      switch (currentStep) {
        case 0:
          return (
            <InformacoesPessoaisCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 1:
          return (
            <EnderecoEContatoCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 2:
          return (
            <DadosDePassaporteIdentidade
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 3:
          return (
            <InformacoesFamiliaCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 4:
          return (
            <InformacoesDeViagemCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 5:
          return (
            <InformacoesComplementaresCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 6:
          return (
            <OcupacaoAtualCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 7:
          return (
            <OcupacaoAnteriorCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 8:
          return (
            <InformacoesAdicionaisCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        case 9:
          return (
            <DocumentosCanada
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        default:
          return null;
      }
    } else if (tipoDs === "australia") {
      // Lógica específica para o formulário do Canadá, se necessário
      switch (currentStep) {
        case 0:
          return (
            <PersonalInformation1
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        default:
          return null;
      }
    } else if (tipoDs === "i140") {
      // Lógica específica para o formulário do Canadá, se necessário
      switch (currentStep) {
        case 0:
          return (
            <PersonalInformation1
              formData={formData}
              handleInputChange={handleInputChange}
              errors={errors}
              addEntry={addEntry}
              removeEntry={removeEntry}
            />
          );
        default:
          return null;
      }
    }

    // Lógica existente para tipos 'adulto' e 'kid'
    switch (currentStep) {
      case 0:
        return (
          <PersonalInformation1
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 1:
        return (
          <InformacoesDeViagem
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 2:
        return (
          <ResidenciaEVistosEUA
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
            tipo={tipoDs}
          />
        );
      case 3:
        return (
          <EnderecoEContato
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 4:
        return (
          <DadosDePassaporte
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
          />
        );

      case 5:
        return (
          <InformacoesFamilia
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 6: // Ocupação Atual
        if (tipoDs === "kid") return null;
        return (
          <OcupacaoAtual
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 7: // Formações Acadêmicas
        if (tipoDs === "kid") return null;
        return (
          <FormacoesAcademicas
            formData={formData}
            handleInputChange={handleInputChange}
            errors={errors}
            addEntry={addEntry}
            removeEntry={removeEntry}
          />
        );
      case 8: // Informações Adicionais
        if (tipoDs === "kid") return null;
        return (
          <InformacoesAdicionais
            formData={formData}
            handleInputChange={handleInputChange}
            addEntry={addEntry}
            removeEntry={removeEntry}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  if (!formReady) {
    return (
      <div className={styles.app}>
        <NotificationContainer />
        {loadState.error &&
        loadState.formId === formId &&
        loadState.linkToken === linkToken ? (
          <div role="alert">
            <div
              style={{
                minHeight: "300px",
                paddingTop: "50px",
                textAlign: "center",
                gap: "20px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <h1 style={{ color: "var(--corPrimaria)", fontSize: "2rem", fontWeight: "bold" }}>Não foi possível abrir este link.</h1>
              <p style={{ color: "var(--corPrimaria)", fontSize: "1.2rem", fontWeight: "bold" }}>
                Abra diretamente o link individual enviado pela assessoria via
                WhatsApp. Ou entre em contato para solicitar novo link
              </p>
              {linkToken && (
                <a href={`/formulario/${encodeURIComponent(linkToken)}`} >
                  Voltar para a área do cliente
                </a>
              )}
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                    backgroundColor: "var(--corPrimaria)",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    fontWeight: "bold",
                }}
                onClick={() =>
                  window.open(
                    "https://api.whatsapp.com/send?phone=5561998819381&text=Oi%2C%20preciso%20de%20ajuda%20para%20acessar%20o%20meu%20formul%C3%A1rio.",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Solicitar novo link via WhatsApp
              </button>
            </div>
          </div>
        ) : (
          <p role="status">Carregando formulário...</p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.app}>
      <NotificationContainer />

      <div style={{ display: "flex", justifyContent: "center" }}>
        <button
          type="button"
          style={{
            margin: "10px",
            display: "flex",
            width: "250px",
            maxWidth: "300px",
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
          }}
          disabled={!clientId || returningToClient}
          onClick={voltarParaAreaCliente}
        >
          <div
            style={{
              width: "100%",
              backgroundColor: "var(--corPrimaria)",
              borderRadius: "10px",
            }}
          >
            <p
              style={{
                color: "white",
                fontWeight: "800px",
                fontSize: "1rem",
                padding: "10px",
              }}
            >
              Voltar para Área do Cliente
            </p>
          </div>
        </button>
      </div>

      <div className={styles.formContainer}>
        <div className={styles.progressBar}>
          {steps.map((step, index) => (
            <div
              key={index}
              className={`${styles.progressStep} ${index === currentStep ? styles.activeStep : ""} ${getStatusClass(index)}`}
              onClick={() => goToStep(index)} // Redireciona ao clicar na etapa
            >
              <span>{index + 1}</span>
            </div>
          ))}
        </div>
        <div className={styles.formContainer2}>
          <form onSubmit={handleFormSubmit}>
            {renderStep()}
            <div className={styles.navigationButtons}>
              {currentStep > 0 && (
                <button type="button" onClick={prevStep}>
                  Voltar
                </button>
              )}
              <button type="submit" style={{ backgroundColor: "#26b709" }}>
                Salvar
              </button>
              {currentStep < steps.length - 1 && (
                <button type="button" onClick={nextStep}>
                  Próximo
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default FormularioCliente;
